import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_ISLAND_KEY } from 'alakai-aloha-shared';
import { getHeroBackgroundLibrary } from './heroBackgrounds';

const HERO_ID_QUERY_PARAM = 'hero';
const HERO_INDEX_QUERY_PARAM = 'heroIndex';
const DEFAULT_STORAGE_NAMESPACE = 'alakai-homepage-hero';

const clampHeroIndex = (rawValue, length) => {
  const numericValue = Number.parseInt(rawValue, 10);
  if (!Number.isInteger(numericValue)) {
    return null;
  }

  if (numericValue >= 1 && numericValue <= length) {
    return numericValue - 1;
  }

  if (numericValue === 0 && length > 0) {
    return 0;
  }

  return null;
};

const getForcedHeroEntry = (entries) => {
  if (typeof window === 'undefined' || entries.length === 0) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const heroId = params.get(HERO_ID_QUERY_PARAM);
  if (heroId) {
    const matchingEntry = entries.find((entry) => entry.id === heroId);
    if (matchingEntry) {
      return matchingEntry;
    }
  }

  const heroIndex = clampHeroIndex(params.get(HERO_INDEX_QUERY_PARAM) || heroId, entries.length);
  return heroIndex === null ? null : entries[heroIndex];
};

const pickWeightedHeroEntry = (entries, excludedHeroId = null) => {
  const eligibleEntries = entries.length > 1
    ? entries.filter((entry) => entry.id !== excludedHeroId)
    : entries;

  if (eligibleEntries.length === 0) {
    return entries[0] || null;
  }

  const totalWeight = eligibleEntries.reduce((sum, entry) => sum + Math.max(1, entry.weight || 1), 0);
  let cursor = Math.random() * totalWeight;

  for (const entry of eligibleEntries) {
    cursor -= Math.max(1, entry.weight || 1);
    if (cursor <= 0) {
      return entry;
    }
  }

  return eligibleEntries[eligibleEntries.length - 1];
};

const resolveInitialHeroEntry = (
  entries,
  {
    storageKey,
  } = {},
) => {
  if (entries.length === 0) {
    return null;
  }

  const forcedHeroEntry = getForcedHeroEntry(entries);
  if (forcedHeroEntry) {
    return forcedHeroEntry;
  }

  if (typeof window === 'undefined') {
    return entries[0];
  }

  const previousHeroId = storageKey
    ? window.sessionStorage.getItem(storageKey)
    : null;

  return pickWeightedHeroEntry(entries, previousHeroId);
};

export const useIslandHeroBackgroundImage = (
  islandKey = DEFAULT_ISLAND_KEY,
  {
    surface = 'homepage',
    minimumReadyCount,
    storageNamespace = DEFAULT_STORAGE_NAMESPACE,
  } = {},
) => {
  const heroLibrary = useMemo(
    () => getHeroBackgroundLibrary(islandKey, { surface, minimumReadyCount }),
    [islandKey, minimumReadyCount, surface],
  );
  const heroEntries = heroLibrary.entries;
  const storageKey = `${storageNamespace}:${surface}:${heroLibrary.sourceIslandKey}`;
  const [currentHeroEntry, setCurrentHeroEntry] = useState(() => resolveInitialHeroEntry(
    heroEntries,
    { storageKey },
  ));
  const lastLibraryKeyRef = useRef(`${heroLibrary.sourceIslandKey}:${surface}:${heroEntries.length}`);

  useEffect(() => {
    if (heroEntries.length === 0) {
      setCurrentHeroEntry(null);
      return;
    }

    const libraryKey = `${heroLibrary.sourceIslandKey}:${surface}:${heroEntries.length}`;
    const libraryChanged = lastLibraryKeyRef.current !== libraryKey;
    lastLibraryKeyRef.current = libraryKey;

    const nextHeroEntry = libraryChanged
      ? resolveInitialHeroEntry(heroEntries, { storageKey })
      : currentHeroEntry;

    if (nextHeroEntry?.id !== currentHeroEntry?.id) {
      setCurrentHeroEntry(nextHeroEntry);
      return;
    }

    if (nextHeroEntry?.id) {
      window.sessionStorage.setItem(storageKey, nextHeroEntry.id);
    }
  }, [currentHeroEntry, heroEntries, heroLibrary.sourceIslandKey, storageKey, surface]);

  return {
    currentHeroEntry,
    heroEntries,
    heroLibrary,
  };
};
